import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n";
import {
  ArrowLeft, ArrowRight, BookOpen, Search as SearchIcon, Compass, Wallet,
  Shield, Anchor, Ship, CalendarDays, Tag, MessageCircle, Clock, Sun,
} from "lucide-react";

/** Tag → icon map for visual variety on the index */
const TAG_ICON = {
  "seamatch": Compass,
  "come-funziona": BookOpen,
  "onboarding": BookOpen,
  "alternative-intelligenti": Compass,
  "consulenza": MessageCircle,
  "budget": Wallet,
  "prezzi": Wallet,
  "mediterraneo": Ship,
  "patente": Shield,
  "skipper": Anchor,
  "documenti": Shield,
  "tipologia-barca": Ship,
  "vela": Ship,
  "catamarano": Ship,
  "motore": Ship,
  "periodo": CalendarDays,
  "stagionalita": CalendarDays,
  "cabin-charter": Anchor,
  "barca-intera": Ship,
  "formula": BookOpen,
  "costi-extra": Wallet,
  "cambusa": Wallet,
  "ormeggi": Anchor,
  "carburante": Wallet,
  "sicurezza": Shield,
  "assicurazione": Shield,
  "cauzione": Shield,
  "pagamenti": Wallet,
  "privacy": Shield,
  "gdpr": Shield,
};

function pickIcon(tags = []) {
  for (const t of tags) {
    if (TAG_ICON[t]) return TAG_ICON[t];
  }
  return BookOpen;
}

/** Minimal markdown → HTML renderer for **bold**, lists, paragraphs, ✔/✘ items.
 *  Kept inline (no extra dep) since content is curated server-side.
 */
function renderContent(text) {
  if (!text) return null;
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, i) => {
    // Callout block: every line starts with "> "
    const lines = block.split("\n");
    if (lines.length > 0 && lines.every(l => l.trim() === "" || /^\s*>\s?/.test(l))) {
      const inner = lines.map(l => l.replace(/^\s*>\s?/, "")).join("\n");
      const innerBlocks = inner.split(/\n\n+/);
      return (
        <aside
          key={i}
          data-testid="kb-callout"
          className="my-8 rounded-2xl border border-gold/40 bg-gradient-to-br from-[#FAF7F0] to-white px-6 py-6 sm:px-8 sm:py-7 shadow-[0_2px_24px_-12px_rgba(196,162,108,0.4)]"
        >
          {innerBlocks.map((b, k) => (
            <p key={k} className="text-ink leading-relaxed whitespace-pre-line [&:not(:first-child)]:mt-3"
               dangerouslySetInnerHTML={{ __html: inlineFormat(b) }} />
          ))}
        </aside>
      );
    }
    // Bullet list block
    if (/^\s*[-•]\s+/m.test(block) && block.split("\n").every(l => l.trim() === "" || /^\s*[-•]\s+/.test(l))) {
      const items = block.split("\n").filter(l => l.trim()).map(l => l.replace(/^\s*[-•]\s+/, ""));
      return (
        <ul key={i} className="list-disc pl-6 space-y-2 my-4 text-ink leading-relaxed">
          {items.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: inlineFormat(it) }} />
          ))}
        </ul>
      );
    }
    // Numbered block (multi-line starting with "N. ")
    if (/^\d+\.\s+/.test(block)) {
      return (
        <p key={i} className="my-4 text-ink leading-relaxed whitespace-pre-line"
           dangerouslySetInnerHTML={{ __html: inlineFormat(block) }} />
      );
    }
    return (
      <p key={i} className="my-4 text-ink leading-relaxed whitespace-pre-line"
         dangerouslySetInnerHTML={{ __html: inlineFormat(block) }} />
    );
  });
}

function inlineFormat(s) {
  // Escape HTML first
  const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-navy'>$1</strong>")
    .replace(/^✔\s/gm, "<span class='text-emerald-600 font-semibold'>✔ </span>")
    .replace(/^✘\s/gm, "<span class='text-rose-600 font-semibold'>✘ </span>")
    .replace(/(\binfo@seamatch\.pro\b)/g, "<a href='mailto:info@seamatch.pro' class='text-navy underline underline-offset-2'>$1</a>");
}

/* ------------------------------ INDEX PAGE ------------------------------ */
const SECTIONS_IT = [
  {
    id: "capire-seamatch",
    eyebrow: "Sezione 01",
    title: "Capire Seamatch",
    desc: "Come funziona il nostro marketplace, perché propone alternative intelligenti e come tutela il tuo viaggio.",
    matchTags: ["seamatch", "come-funziona", "onboarding", "alternative-intelligenti", "consulenza", "pagamenti", "privacy", "gdpr"],
  },
  {
    id: "scegliere-prenotare",
    eyebrow: "Sezione 02",
    title: "Scegliere e prenotare",
    desc: "Budget realistici, tipologie di barca, formule di noleggio. Tutto quello che serve per decidere bene.",
    matchTags: ["budget", "prezzi", "patente", "skipper", "documenti", "tipologia-barca", "vela", "catamarano", "motore", "cabin-charter", "barca-intera", "formula"],
  },
  {
    id: "a-bordo",
    eyebrow: "Sezione 03",
    title: "Vivere il viaggio",
    desc: "Stagionalità, costi extra a bordo, sicurezza e assicurazioni. I dettagli che fanno la differenza.",
    matchTags: ["periodo", "stagionalita", "mediterraneo", "costi-extra", "cambusa", "ormeggi", "carburante", "sicurezza", "assicurazione", "cauzione"],
  },
];

const SECTIONS_EN = [
  {
    id: "the-seamatch-way",
    eyebrow: "Chapter 01",
    title: "The Seamatch way",
    desc: "How a private yacht concierge works — and why every holiday is hand-built around the people on board.",
    matchTags: ["seamatch", "how-it-works", "philosophy", "concierge"],
  },
  {
    id: "choosing-your-yacht",
    eyebrow: "Chapter 02",
    title: "Choosing your yacht",
    desc: "Indicative pricing, yacht types, sailing licences and on-board service levels — quietly explained.",
    matchTags: ["pricing", "charter", "yacht", "selection", "skipper", "crew", "service", "licence", "regulation"],
  },
  {
    id: "on-board",
    eyebrow: "Chapter 03",
    title: "Life on board",
    desc: "Destinations, seasonality, packing notes, payments and security — the details that shape a quietly perfect holiday.",
    matchTags: ["destinations", "mediterranean", "season", "packing", "before-you-sail", "advice", "payments", "security", "contact", "support"],
  },
];

function groupBySection(docs, sectionsConfig, leftoverLabels) {
  const used = new Set();
  const grouped = sectionsConfig.map(sec => {
    const items = docs.filter(d => {
      if (used.has(d.id)) return false;
      const has = (d.tags || []).some(t => sec.matchTags.includes(t));
      if (has) used.add(d.id);
      return has;
    });
    return { ...sec, items };
  });
  // Leftover bucket
  const leftover = docs.filter(d => !used.has(d.id));
  if (leftover.length) {
    grouped.push({
      id: "other",
      eyebrow: leftoverLabels.eyebrow,
      title: leftoverLabels.title,
      desc: leftoverLabels.desc,
      items: leftover,
    });
  }
  return grouped.filter(g => g.items.length > 0);
}

function KbCard({ d }) {
  const Icon = pickIcon(d.tags);
  const { lang, localizePath } = useI18n();
  const EN = lang === "en";
  return (
    <Link
      to={localizePath(`/centro-aiuto/${d.slug}`)}
      data-testid={`kb-card-${d.slug}`}
      className="group block bg-white border border-[#E2E8F0] rounded-2xl p-6 hover:border-navy/40 hover:shadow-md transition-all"
    >
      <div className="h-10 w-10 rounded-xl bg-sand flex items-center justify-center text-navy group-hover:bg-navy group-hover:text-white transition">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 font-serif text-lg text-navy leading-snug group-hover:text-gold transition">
        {d.title}
      </h3>
      {d.reading_time > 0 && (
        <p className="mt-2 inline-flex items-center text-[10px] text-ink/60">
          <Clock className="h-3 w-3 mr-1 text-gold" />
          {d.reading_time} {EN ? "min read" : "min di lettura"}
        </p>
      )}
      {d.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {d.tags.slice(0, 3).map(t => (
            <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sand text-ink/70">
              {t}
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 inline-flex items-center text-xs font-semibold text-navy">
        {EN ? "Read the guide" : "Leggi la guida"}
        <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition" />
      </div>
    </Link>
  );
}

function CentroAiutoIndex() {
  const { lang, localizePath } = useI18n();
  const EN = lang === "en";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/kb/public?lang=${lang}`)
      .then(r => { if (!cancel) setDocs(r.data || []); })
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [lang]);

  const filtered = useMemo(() => {
    if (!query.trim()) return docs;
    const q = query.toLowerCase();
    return docs.filter(d =>
      (d.title || "").toLowerCase().includes(q) ||
      (d.tags || []).some(t => t.toLowerCase().includes(q))
    );
  }, [docs, query]);

  const sections = useMemo(() => {
    const cfg = EN ? SECTIONS_EN : SECTIONS_IT;
    const leftover = EN
      ? { eyebrow: "More", title: "More guides", desc: "Further notes from our concierge team." }
      : { eyebrow: "Altro", title: "Altre guide", desc: "Approfondimenti utili per la tua vacanza in barca." };
    return groupBySection(filtered, cfg, leftover);
  }, [filtered, EN]);
  const isSearching = query.trim().length > 0;

  return (
    <div data-testid="centro-aiuto-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sand via-white to-white" />
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{EN ? "Sailing guide" : "Prima di salpare"}</p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.05]">
            {EN ? "Notes from our concierge team," : "Tutto quello che ti serve sapere"}
            <br />
            <em className="italic text-gold font-medium">{EN ? "before you set sail." : "prima di salpare."}</em>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-ink leading-relaxed">
            {EN
              ? "Quietly curated essays, checklists and seasonal advice from the specialists who design every Seamatch holiday. For when you'd like to know more before we begin."
              : "Guide chiare, costi reali, consigli di chi vive il mare ogni giorno. Hai una domanda specifica? Scrivici, rispondiamo entro 24 ore."}
          </p>

          <div className="mt-8 max-w-xl">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/50" />
              <Input
                data-testid="kb-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={EN ? "Search the guide: itineraries, skipper, harbours…" : "Cerca per parola chiave: budget, skipper, Eolie…"}
                className="pl-9 h-12 rounded-full border-navy/20 bg-white"
              />
            </div>
          </div>

          {/* Section anchor chips — only when not searching */}
          {!isSearching && !loading && sections.length > 1 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="kb-section-chips">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`kb-chip-${s.id}`}
                  className="inline-flex items-center text-xs font-semibold px-3 py-1.5 rounded-full border border-navy/15 text-navy bg-white hover:bg-navy hover:text-white transition"
                >
                  {s.title}
                  <span className="ml-1.5 text-[10px] opacity-60">{s.items.length}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECTIONS */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          {loading ? (
            <div className="text-center text-ink/60 py-12">{EN ? "Loading…" : "Caricamento…"}</div>
          ) : filtered.length === 0 ? (
            isSearching ? (
              <div data-testid="kb-empty" className="text-center text-ink/60 py-12">
                {EN ? "No guide matches your search. Try a different keyword." : "Nessuna guida corrisponde alla tua ricerca. Prova con un'altra parola chiave."}
              </div>
            ) : (
              <div data-testid="kb-empty-no-data" className="text-center py-16">
                <p className="font-serif text-2xl text-navy">{EN ? "Guides are coming." : "Le guide stanno arrivando."}</p>
                <p className="mt-3 text-sm text-ink/60 max-w-md mx-auto">
                  {EN
                    ? <>We're curating the Sailing Guide content. Come back soon — or write to <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> to talk to a concierge.</>
                    : <>Stiamo preparando i contenuti del Centro Aiuto. Torna a trovarci tra poco — oppure scrivici a <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> per parlare con un consulente.</>}
                </p>
              </div>
            )
          ) : isSearching ? (
            // Flat list when searching
            <div data-testid="kb-search-results" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((d) => <KbCard key={d.id} d={d} />)}
            </div>
          ) : (
            <div className="space-y-16">
              {sections.map((sec) => (
                <div key={sec.id} id={sec.id} data-testid={`kb-section-${sec.id}`} className="scroll-mt-24">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-7">
                    <div className="max-w-2xl">
                      <p className="label-eyebrow">{sec.eyebrow}</p>
                      <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-navy leading-tight">{sec.title}</h2>
                      <p className="mt-2 text-sm text-ink/70">{sec.desc}</p>
                    </div>
                    <div className="hidden md:flex items-center text-xs uppercase tracking-wider text-ink/50">
                      <span className="h-px w-12 bg-navy/20 mr-2" />
                      {sec.items.length} {EN ? (sec.items.length === 1 ? "guide" : "guides") : (sec.items.length === 1 ? "guida" : "guide")}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {sec.items.map((d) => <KbCard key={d.id} d={d} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white py-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="label-eyebrow text-gold">{EN ? "Didn't find what you needed?" : "Non hai trovato risposta?"}</p>
            <h2 className="mt-3 font-serif text-3xl">{EN ? "Speak with your Seamatch concierge." : "Parla con un consulente Seamatch."}</h2>
            <p className="mt-3 text-white/70 max-w-xl">
              {EN ? "Every brief is read and answered personally by a member of our team — typically within a few hours." : "Rispondiamo a ogni messaggio entro 24 ore. Niente call center, persone vere."}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="kb-cta-contact"
              onClick={() => navigate(localizePath("/contatti"))}
              className="bg-white text-navy hover:bg-sand rounded-full h-12 px-6"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {EN ? "Speak with a concierge" : "Contattaci"}
            </Button>
            <Button
              data-testid="kb-cta-search"
              onClick={() => navigate(localizePath("/search"))}
              variant="outline"
              className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
            >
              {EN ? "Brief my concierge" : "Inizia la richiesta"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------ DETAIL PAGE ------------------------------ */
function CentroAiutoDoc() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, localizePath } = useI18n();
  const EN = lang === "en";
  const [doc, setDoc] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      let detailRes = null;
      let listRes = { data: [] };
      let nf = false;
      try {
        detailRes = await api.get(`/kb/public/${slug}?lang=${lang}`);
      } catch (err) {
        if (err?.response?.status === 404) nf = true;
      }
      try {
        listRes = await api.get(`/kb/public?lang=${lang}`);
      } catch (e) { /* noop */ }
      if (cancel) return;
      setNotFound(nf);
      setDoc(detailRes?.data || null);
      setRelated((listRes?.data || []).filter(x => x.slug !== slug).slice(0, 4));
      setLoading(false);
    })();
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => { cancel = true; };
  }, [slug, lang]);

  if (loading) {
    return <div className="pt-32 text-center text-ink/60 min-h-screen">{EN ? "Loading…" : "Caricamento…"}</div>;
  }

  if (notFound || !doc) {
    return (
      <div data-testid="kb-not-found" className="pt-32 pb-20 min-h-screen">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 text-center">
          <h1 className="font-serif text-3xl text-navy">{EN ? "Document not found" : "Documento non trovato"}</h1>
          <p className="mt-3 text-ink/70">{EN ? "The guide you're looking for may have been moved or removed." : "La guida che cerchi potrebbe essere stata spostata o rimossa."}</p>
          <Button onClick={() => navigate(localizePath("/centro-aiuto"))} className="mt-6 bg-navy text-white hover:bg-[#06101f] rounded-full h-11 px-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> {EN ? "Back to Sailing Guide" : "Torna a Prima di salpare"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="kb-detail-page" className="bg-white">
      <article className="pt-28 pb-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <button
            data-testid="kb-back-btn"
            onClick={() => navigate(localizePath("/centro-aiuto"))}
            className="inline-flex items-center text-sm text-ink/60 hover:text-navy transition"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> {EN ? "Sailing guide" : "Prima di salpare"}
          </button>

          <p className="label-eyebrow mt-6">{EN ? "Seamatch guide" : "Guida Seamatch"}</p>
          <h1 data-testid="kb-detail-title" className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.1] tracking-tight">
            {doc.title}
          </h1>

          {/* Editorial meta strip (reading time + optional best_period / budget / boat type) */}
          {(doc.reading_time || doc.best_period || doc.budget_range || doc.ideal_boat_type) && (
            <div
              data-testid="kb-meta-strip"
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink/70 border-t border-b border-[#E5E1D2] py-3"
            >
              {doc.reading_time > 0 && (
                <span data-testid="kb-meta-reading-time" className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-gold" />
                  <span className="font-semibold text-navy">{doc.reading_time} {EN ? "min read" : "min di lettura"}</span>
                </span>
              )}
              {doc.best_period && (
                <span data-testid="kb-meta-best-period" className="inline-flex items-center gap-1.5">
                  <Sun className="h-3.5 w-3.5 text-gold" />
                  <span className="text-ink/60">{EN ? "Best period:" : "Periodo migliore:"}</span>
                  <span className="font-semibold text-navy">{doc.best_period}</span>
                </span>
              )}
              {doc.budget_range && (
                <span data-testid="kb-meta-budget" className="inline-flex items-center gap-1.5">
                  <Wallet className="h-3.5 w-3.5 text-gold" />
                  <span className="text-ink/60">{EN ? "Budget:" : "Budget:"}</span>
                  <span className="font-semibold text-navy">{doc.budget_range}</span>
                </span>
              )}
              {doc.ideal_boat_type && (
                <span data-testid="kb-meta-boat" className="inline-flex items-center gap-1.5">
                  <Ship className="h-3.5 w-3.5 text-gold" />
                  <span className="text-ink/60">{EN ? "Ideal boat:" : "Barca ideale:"}</span>
                  <span className="font-semibold text-navy">{doc.ideal_boat_type}</span>
                </span>
              )}
            </div>
          )}

          {doc.tags?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {doc.tags.map(t => (
                <span key={t} className="inline-flex items-center text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-sand text-ink/70">
                  <Tag className="h-3 w-3 mr-1" /> {t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-8 prose-seamatch">
            {renderContent(doc.content)}
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-sand border border-[#E2E8F0]">
            <p className="font-serif text-xl text-navy">{EN ? "Still have questions on this topic?" : "Hai ancora dubbi su questo argomento?"}</p>
            <p className="mt-2 text-sm text-ink/70">
              {EN
                ? <>A Seamatch concierge replies within 24 hours. Write to <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> or use the contact form.</>
                : <>Un consulente Seamatch ti risponde entro 24 ore. Scrivici a <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> oppure usa il form di contatto.</>}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate(localizePath("/contatti"))}
                className="bg-navy text-white hover:bg-[#06101f] rounded-full h-10 px-5 text-sm"
              >
                <MessageCircle className="h-4 w-4 mr-2" /> {EN ? "Contact us" : "Contattaci"}
              </Button>
              <Button
                onClick={() => navigate(localizePath("/search"))}
                variant="outline"
                className="rounded-full h-10 px-5 text-sm border-navy/30 text-navy hover:bg-white"
              >
                {EN ? "Plan your trip" : "Inizia la richiesta"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-sand py-16">
          <div className="max-w-6xl mx-auto px-5 lg:px-10">
            <p className="label-eyebrow">{EN ? "Keep reading" : "Continua a leggere"}</p>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-navy">{EN ? "Other guides from the «Sailing guide» section" : "Altre guide della sezione «Prima di salpare»"}</h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map(r => {
                const Icon = pickIcon(r.tags);
                return (
                  <Link
                    key={r.id}
                    to={localizePath(`/centro-aiuto/${r.slug}`)}
                    data-testid={`kb-related-${r.slug}`}
                    className="group block bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:border-navy/40 hover:shadow-md transition"
                  >
                    <Icon className="h-5 w-5 text-navy" />
                    <h3 className="mt-3 font-serif text-base text-navy leading-snug group-hover:text-gold transition">
                      {r.title}
                    </h3>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ------------------------------ ROUTER WRAPPER ------------------------------ */
export default function CentroAiuto() {
  const { slug } = useParams();
  return slug ? <CentroAiutoDoc /> : <CentroAiutoIndex />;
}
